import React, { useState } from 'react';
import { createRoot, Root } from 'react-dom/client';
import MultiStepForm from './components/MultiStepForm/MultiStepForm';
import { useOrganizationData } from './hooks/useOrganizationData';
import { ServiceRequestFormData, FormStep, OrganizationData, OrganizationSettings, OrganizationStatusSetting } from './types';
import { supabase } from './config/supabaseClient';
import { useOrgTheming } from './hooks/useOrgTheming';
import SlimHeader from './components/SlimHeader';

// Import all styles - these will be bundled with the widget
import './styles/app.scss';
import './styles/widget.scss';

type FullOrganizationData = OrganizationData & {
  organizationSettings?: OrganizationSettings | null;
  organizationStatusSettings?: OrganizationStatusSetting[];
};

const formSteps: FormStep[] = [
  { id: 1, title: 'Welcome', iconName: 'fe-info' },
  { id: 2, title: 'Your Details', iconName: 'fe-user' },
  { id: 3, title: 'Location', iconName: 'fe-map-pin' },
  { id: 4, title: 'Machine', iconName: 'fe-settings' },
  { id: 5, title: 'Service Reason', iconName: 'fe-activity' },
  { id: 6, title: 'Select Package', iconName: 'fe-package' },
  { id: 7, title: 'Review & Submit', iconName: 'fe-check-circle' },
];

// Widget Form Component
function WidgetForm({ organizationId }: { organizationId: string }) {
  const { data: orgDataResult, loading: orgLoading, error: orgError } = useOrganizationData(organizationId);
  const [formData, setFormData] = useState<ServiceRequestFormData>({
    selectedLocationId: null,
    distanceToSelectedLocation: null,
    selectedCategoryId: null,
    selectedDisplayCategoryId: null,
    selectedMakeId: null,
    selectedMachineId: null,
    selectedStatus: null,
    selectedPackageId: null,
    reviewConfirmed: false,
    shopServicePreference: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const handleStepChange = React.useCallback((index: number, total: number) => {
    setProgress(total > 0 ? (index + 1) / total : 0);
  }, []);

  useOrgTheming(
    orgDataResult?.organizationSettings?.primary_color,
    document.querySelector('opervia-form') as HTMLElement | null,
  );

  const handleFormSubmit = async (finalFormData: ServiceRequestFormData) => {
    setIsSubmitting(true);
    setSubmissionError(null);

    if (!organizationId) {
      setSubmissionError("Organization ID is missing. Cannot submit.");
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        organizationId: parseInt(organizationId, 10),
        formData: { ...finalFormData, source: 'public_form' }
      };

      const { data, error: functionError } = await supabase.functions.invoke('create-service-request', {
        body: payload,
      });

      if (functionError) {
        throw new Error(`Function invocation failed: ${functionError.message}`);
      }
      if (data?.success) {
        setSubmissionSuccess(true);
        setFormData({
          selectedLocationId: null,
          distanceToSelectedLocation: null,
          selectedCategoryId: null,
          selectedMakeId: null,
          selectedMachineId: null,
          selectedStatus: null,
          selectedPackageId: null,
          reviewConfirmed: false,
          shopServicePreference: null
        });

        // Dispatch custom event for parent page
        window.dispatchEvent(new CustomEvent('opervia-form-success', {
          detail: {
            requestId: data.serviceRequestId,
            customerId: data.customerUUID
          }
        }));
      } else {
        throw new Error(data?.error || 'Submission failed due to an unknown server error.');
      }
    } catch (submissionError: any) {
      console.error("Submission failed:", submissionError);
      setSubmissionError(`Submission failed: ${submissionError.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orgLoading) {
    return <div className="loading-indicator">Loading Form...</div>;
  }

  if (orgError) {
    return <div className="error-message">Error: {orgError}</div>;
  }

  if (!orgDataResult?.organization) {
    return <div className="error-message">Form configuration not found or organization missing.</div>;
  }

  if (submissionSuccess) {
    return (
      <div className="public-form-page opervia-widget">
        <div className="public-form-app-container slim-shell">
          <SlimHeader
            logoUrl={orgDataResult.organizationSettings?.logo_url}
            orgName={orgDataResult.organization.name}
            title="Service Request"
          />
          <div className="success-message">
            <h2>Thank you!</h2>
            <p>Your service request has been submitted successfully.</p>
            <button
              className="btn btn-primary"
              onClick={() => setSubmissionSuccess(false)}
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fullOrgData: FullOrganizationData | null = orgDataResult;

  return (
    <div className="public-form-page opervia-widget">
      <div className="public-form-app-container slim-shell">
        <SlimHeader
          logoUrl={orgDataResult.organizationSettings?.logo_url}
          orgName={orgDataResult.organization.name}
          title="Service Request"
          progress={progress}
        />

        {submissionError && (
          <div className="error-message summary-error submission-error">
            <strong>Submission Failed:</strong> {submissionError}
          </div>
        )}

        {fullOrgData && (
          <MultiStepForm
            steps={formSteps}
            formData={formData}
            setFormData={setFormData}
            organizationData={fullOrgData}
            onSubmit={handleFormSubmit}
            onStepChange={handleStepChange}
          />
        )}

        {isSubmitting && <div className="submitting-overlay">Submitting...</div>}
      </div>
    </div>
  );
}

// Custom Element Definition
class OperviaFormElement extends HTMLElement {
  private root: Root | null = null;
  private mountPoint: HTMLDivElement | null = null;

  static get observedAttributes() {
    return ['org-id', 'theme'];
  }

  connectedCallback() {
    const orgId = this.getAttribute('org-id');

    if (!orgId) {
      console.error('Opervia Form Widget: org-id attribute is required');
      this.innerHTML = '<div style="padding: 20px; color: red;">Error: org-id attribute is required</div>';
      return;
    }

    // Create mount point
    this.mountPoint = document.createElement('div');
    this.mountPoint.className = 'opervia-form-widget-root';
    this.appendChild(this.mountPoint);

    // Mount React app
    this.root = createRoot(this.mountPoint);
    this.root.render(
      <React.StrictMode>
        <WidgetForm organizationId={orgId} />
      </React.StrictMode>
    );

    // Notify parent that widget is loaded
    window.dispatchEvent(new CustomEvent('opervia-form-loaded'));
  }

  disconnectedCallback() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'org-id' && oldValue !== newValue && this.root && this.mountPoint) {
      // Re-render with new org ID
      this.root.render(
        <React.StrictMode>
          <WidgetForm organizationId={newValue} />
        </React.StrictMode>
      );
    }
  }
}

// Register the custom element
if (!customElements.get('opervia-form')) {
  customElements.define('opervia-form', OperviaFormElement);
}

// Export for module usage
export { OperviaFormElement, WidgetForm };

// Auto-initialize if script is loaded traditionally (not as module)
if (typeof window !== 'undefined') {
  console.log('Opervia Form Widget loaded successfully');
}
